/**
 * InboxPage - WhatsApp conversation inbox
 * Features: Real message history, Thread list, Conversation view, Send messages
 *
 * Data flow:
 * - OUTGOING (sent by us): MessageTracking model → type: 'sent'
 * - INCOMING (received from user): UserMessage model → type: 'received'
 * - Combined via: /api/events/:eventId/message-outbox API
 */

import React, { useState, useEffect, useRef } from 'react';
import { userMessageService, customMessageService } from '../../services/api';
import toast from 'react-hot-toast';

// Helpers
const initials = (n) => n ? n.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase() : '??';
const timeFormat = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const diff = now - date;
  if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diff < 172800000) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const fullTimeFormat = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

// Message type labels
const messageTypeLabel = (type) => {
  const labels = {
    'opt_in_template': 'Opt-in Request',
    'task_notification': 'Task Assignment',
    'vendor_notification': 'Vendor Assignment',
    'custom_message': 'Custom Message',
    'rsvp_reminder': 'RSVP Reminder',
    'AI_response': 'AI Reply',
    'regular_message': 'Message',
  };
  return labels[type] || type?.replace(/_/g, ' ');
};

// Icon component
const Icon = ({ name, size = 16 }) => {
  const icons = {
    search: <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    sparkles: <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></>,
    send: <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
    inbox: <><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></>,
    refresh: <><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></>,
    check: <><polyline points="20 6 9 17 4 12"/></>,
    checkDouble: <><path d="M18 6 7 17l-5-5"/><path d="m22 10-7.5 7.5L13 16"/></>,
    x: <><path d="M18 6 6 18M6 6l12 12"/></>,
    alertCircle: <><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name] || icons.inbox}
    </svg>
  );
};

// Delivery status indicator
const DeliveryStatus = ({ status }) => {
  if (status === 'delivered' || status === 'read') {
    return <Icon name="checkDouble" size={12} />;
  }
  if (status === 'sent') {
    return <Icon name="check" size={12} />;
  }
  if (status === 'failed' || status === 'undelivered') {
    return <span style={{ color: 'hsl(0 70% 50%)' }}><Icon name="alertCircle" size={12} /></span>;
  }
  return null;
};

// ============ INBOX PAGE ============
function InboxPage({ event, onRefresh, eventId }) {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);
  const activePhoneRef = useRef(null);
  const conversationsRef = useRef('');

  // Initial fetch (shows loading spinner)
  useEffect(() => {
    if (eventId) {
      fetchConversations();
    }
  }, [eventId]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const data = await userMessageService.getMessageOutbox(eventId, 200);
      const newConvs = data.conversations || [];
      setConversations(newConvs);
      conversationsRef.current = JSON.stringify(newConvs);

      // Restore active conversation if one was selected
      if (activePhoneRef.current) {
        const updated = newConvs.find(c => c.phoneNumber === activePhoneRef.current);
        if (updated) setActiveConversation(updated);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Soft refresh — background fetch, no loading state, diff comparison
  const softRefresh = async () => {
    try {
      const data = await userMessageService.getMessageOutbox(eventId, 200);
      const newConvs = data.conversations || [];
      const newDataStr = JSON.stringify(newConvs);

      if (newDataStr === conversationsRef.current) return; // No changes

      // Check if any conversation has new messages (not just read-status changes)
      const oldConvs = JSON.parse(conversationsRef.current || '[]');
      const oldTotalMessages = oldConvs.reduce((sum, c) => sum + (c.messages?.length || 0), 0);
      const newTotalMessages = newConvs.reduce((sum, c) => sum + (c.messages?.length || 0), 0);
      const hasNewMessages = newTotalMessages > oldTotalMessages;

      conversationsRef.current = newDataStr;
      setConversations(newConvs);

      // Update active conversation only if its messages changed (new message arrived)
      if (activePhoneRef.current) {
        const updatedConv = newConvs.find(c => c.phoneNumber === activePhoneRef.current);
        if (updatedConv) {
          setActiveConversation(prev => {
            // Only replace if message count changed (new message arrived, not just read status)
            if (!prev || updatedConv.messages.length !== prev.messages.length) {
              return updatedConv;
            }
            return prev;
          });
        }
      }

      // Only toast when actual new messages arrived, not just read-status changes
      if (hasNewMessages) {
        toast('New messages', { duration: 2000, position: 'bottom-right' });
      }
    } catch (err) {
      // Silent failure for background refresh
      console.error('Soft refresh failed:', err);
    }
  };

  // 15s polling with visibility gating
  useEffect(() => {
    if (!eventId) return;
    let intervalId;

    const startPolling = () => {
      intervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          softRefresh();
        }
      }, 15000);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        softRefresh(); // Immediate refresh on tab return
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [eventId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeConversation?.messages]);

  // Filter conversations
  const filteredConversations = conversations.filter(c =>
    c.contactName?.toLowerCase().includes(search.toLowerCase()) ||
    c.phoneNumber?.includes(search)
  );

  // Select conversation — purely local, no network calls
  const selectConversation = (conv) => {
    activePhoneRef.current = conv.phoneNumber;

    // If unread, do optimistic local update + fire-and-forget API calls
    if (conv.unreadCount > 0) {
      const markedConv = {
        ...conv,
        unreadCount: 0,
        messages: conv.messages.map(m => m.type === 'received' && m.status === 'unread' ? { ...m, status: 'read' } : m)
      };
      setActiveConversation(markedConv);

      // Update conversation list in-place
      setConversations(prev => {
        const updated = prev.map(c => c.phoneNumber === conv.phoneNumber ? markedConv : c);
        conversationsRef.current = JSON.stringify(updated);
        return updated;
      });

      // Bulk mark all messages in this conversation as read
      userMessageService.markConversationRead(eventId, conv.phoneNumber).catch(() => {});
    } else {
      setActiveConversation(conv);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!messageText.trim() || !activeConversation) return;

    setSending(true);
    try {
      // Find guest ID for this phone number
      const guest = event?.guests?.find(g => g.whatsappNumber === activeConversation.phoneNumber);

      await customMessageService.sendCustomMessage(eventId, {
        recipientIds: guest ? [guest._id] : [],
        recipientPhones: guest ? [] : [activeConversation.phoneNumber],
        message: messageText,
      });

      toast.success('Message sent');
      setMessageText('');

      // Refresh to show new message
      setTimeout(softRefresh, 1000);
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Get RSVP status color
  const getRsvpInfo = (phoneNumber) => {
    const guest = event?.guests?.find(g => g.whatsappNumber === phoneNumber);
    if (!guest) return { color: 'hsl(200 20% 60%)', label: 'Unknown' };
    if (guest.rsvp === 'yes') return { color: 'hsl(140 60% 40%)', label: 'Confirmed' };
    if (guest.rsvp === 'no') return { color: 'hsl(0 60% 50%)', label: 'Declined' };
    if (guest.rsvp === 'pending') return { color: 'hsl(45 80% 45%)', label: 'Pending' };
    return { color: 'hsl(200 20% 70%)', label: 'Not Invited' };
  };

  return (
    <div className="inbox" style={{
      display: 'grid',
      gridTemplateColumns: '340px 1fr',
      gap: 0,
      height: 'calc(100vh - 180px)',
      minHeight: 500,
      maxHeight: 'calc(100vh - 180px)'
    }}>
      {/* Thread List */}
      <div className="inbox-list" style={{
        borderRight: '1px solid var(--c-border)',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: '#fff',
        minHeight: 0  // Critical for flex scroll
      }}>
        {/* Search */}
        <div style={{ padding: 16, borderBottom: '1px solid var(--c-border)', display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              placeholder="Search conversations..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px 10px 36px', border: '1px solid var(--c-border)',
                borderRadius: 8, fontSize: 14, outline: 'none'
              }}
            />
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-fg-muted)', pointerEvents: 'none' }}>
              <Icon name="search" size={16} />
            </div>
          </div>
          <button
            onClick={softRefresh}
            disabled={loading}
            style={{
              width: 40, height: 40, border: '1px solid var(--c-border)', borderRadius: 8,
              background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--c-fg-muted)'
            }}
            title="Refresh"
          >
            <Icon name="refresh" size={16} />
          </button>
        </div>

        {/* Conversations */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-fg-muted)' }}>Loading...</div>
        ) : filteredConversations.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-fg-muted)' }}>
            {search ? 'No matches found' : 'No conversations yet'}
            <div style={{ fontSize: 12, marginTop: 8 }}>
              Messages will appear here when guests respond
            </div>
          </div>
        ) : (
          filteredConversations.map(conv => {
            const rsvpInfo = getRsvpInfo(conv.phoneNumber);
            const lastMsg = conv.messages?.[0];

            return (
              <div
                key={conv.phoneNumber}
                onClick={() => selectConversation(conv)}
                style={{
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--c-border)',
                  cursor: 'pointer',
                  background: activeConversation?.phoneNumber === conv.phoneNumber ? 'hsl(170 60% 25% / 0.05)' : 'transparent',
                  display: 'flex', gap: 12, alignItems: 'flex-start'
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${rsvpInfo.color}, ${rsvpInfo.color}dd)`,
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 600, flexShrink: 0
                }}>
                  {initials(conv.contactName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{conv.contactName || conv.phoneNumber}</div>
                    {lastMsg && <div style={{ fontSize: 11, color: 'var(--c-fg-muted)' }}>{timeFormat(lastMsg.timestamp)}</div>}
                  </div>
                  <div style={{
                    fontSize: 13, color: 'var(--c-fg-muted)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    display: 'flex', alignItems: 'center', gap: 4
                  }}>
                    {lastMsg?.type === 'sent' && <DeliveryStatus status={lastMsg.status} />}
                    {lastMsg?.content?.substring(0, 40) || 'No messages yet'}
                    {lastMsg?.content?.length > 40 && '...'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 500,
                      background: rsvpInfo.color + '20',
                      color: rsvpInfo.color,
                    }}>
                      {rsvpInfo.label}
                    </span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 500,
                      background: 'hsl(200 20% 93%)',
                      color: 'hsl(200 20% 40%)',
                    }}>
                      {conv.totalSent} sent · {conv.totalReceived} received
                    </span>
                  </div>
                </div>
                {conv.unreadCount > 0 && (
                  <div style={{
                    minWidth: 20, height: 20, borderRadius: 10,
                    background: 'var(--c-primary)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 600, marginTop: 4
                  }}>
                    {conv.unreadCount}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Conversation Pane */}
      <div className="convo" style={{ display: 'flex', flexDirection: 'column', background: '#fff', minHeight: 0, overflow: 'hidden' }}>
        {!activeConversation ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
            <Icon name="inbox" size={48} />
            <div style={{ color: 'var(--c-fg-muted)' }}>Select a conversation to view messages</div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid var(--c-border)',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: `linear-gradient(135deg, ${getRsvpInfo(activeConversation.phoneNumber).color}, ${getRsvpInfo(activeConversation.phoneNumber).color}dd)`,
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 600
              }}>
                {initials(activeConversation.contactName)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{activeConversation.contactName || 'Unknown'}</div>
                <div style={{ fontSize: 12, color: 'var(--c-fg-muted)' }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', background: '#4caf50',
                    display: 'inline-block', marginRight: 6
                  }} />
                  {activeConversation.phoneNumber} · WhatsApp
                </div>
              </div>
              <div style={{
                padding: '4px 10px', borderRadius: 12,
                background: 'hsl(200 20% 95%)',
                color: 'hsl(200 20% 40%)', fontSize: 11, fontWeight: 500
              }}>
                {activeConversation.totalSent + activeConversation.totalReceived} messages
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, padding: 20, overflowY: 'auto', overflowX: 'hidden', background: 'hsl(200 20% 98%)', minHeight: 0 }}>
              {!activeConversation.messages?.length ? (
                <div style={{ textAlign: 'center', color: 'var(--c-fg-muted)', paddingTop: 40 }}>
                  No messages yet. Send a message to start the conversation.
                </div>
              ) : (
                <>
                  {/* Reverse to show oldest first */}
                  {[...activeConversation.messages].reverse().map((msg, i) => {
                    const isOutgoing = msg.type === 'sent';
                    return (
                      <div key={msg.id || i} style={{
                        display: 'flex',
                        justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                        marginBottom: 12
                      }}>
                        <div style={{
                          maxWidth: '70%',
                        }}>
                          {/* Message type label for outgoing */}
                          {isOutgoing && msg.messageType && (
                            <div style={{
                              fontSize: 10, color: 'var(--c-fg-muted)', marginBottom: 4, textAlign: 'right'
                            }}>
                              {messageTypeLabel(msg.messageType)}
                            </div>
                          )}
                          <div style={{
                            padding: '10px 14px', borderRadius: 12,
                            background: isOutgoing ? 'var(--c-primary)' : '#fff',
                            color: isOutgoing ? '#fff' : 'var(--c-fg)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                          }}>
                            {msg.content}
                            <div style={{
                              fontSize: 10, marginTop: 4, opacity: 0.7, textAlign: 'right',
                              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4
                            }}>
                              {fullTimeFormat(msg.timestamp)}
                              {isOutgoing && <DeliveryStatus status={msg.status} />}
                            </div>
                          </div>
                          {/* Error message for failed sends */}
                          {isOutgoing && (msg.status === 'failed' || msg.status === 'undelivered') && msg.errorMessage && (
                            <div style={{
                              fontSize: 11, color: 'hsl(0 70% 50%)', marginTop: 4, textAlign: 'right'
                            }}>
                              Failed: {msg.errorMessage}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Composer */}
            <div style={{
              padding: 16, borderTop: '1px solid var(--c-border)',
              display: 'flex', gap: 12, alignItems: 'flex-end'
            }}>
              <textarea
                placeholder="Type a message..."
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                rows={1}
                style={{
                  flex: 1, padding: '12px 14px', border: '1px solid var(--c-border)',
                  borderRadius: 12, fontSize: 14, outline: 'none', resize: 'none',
                  fontFamily: 'inherit', minHeight: 44, maxHeight: 120
                }}
              />
              <button
                className="btn-primary"
                onClick={sendMessage}
                disabled={!messageText.trim() || sending}
                style={{ height: 44, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {sending ? 'Sending...' : <Icon name="send" size={16} />}
              </button>
            </div>
            <div style={{
              padding: '8px 16px', fontSize: 11, color: 'var(--c-fg-muted)',
              borderTop: '1px solid var(--c-border)', textAlign: 'center'
            }}>
              Press Enter to send · Shift+Enter for new line
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default InboxPage;
